import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Email+password sign-in / register + "Continue with Google".
 * Matches the editorial b&w palette (no colored gradients).
 */
export default function SignInModal({ open, onClose, defaultMode = "signin", redirectTo = "/tracker" }) {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(defaultMode); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "signin") {
        await login(email.trim(), password);
        toast.success("Welcome back.");
      } else {
        await register(email.trim(), password, inviteCode.trim());
        toast.success("Account created.");
      }
      onClose?.();
      if (redirectTo) {
        // Hard assign so the AuthProvider re-reads /auth/me cleanly on the
        // destination page — eliminates any stale "Sign in" button flashes.
        window.location.assign(redirectTo);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(17,17,17,0.78)" }}
      data-testid="signin-modal"
    >
      <div
        className="relative w-full max-w-md border-2 border-[#111] bg-[#fafaf9] p-7"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="signin-close"
          className="absolute top-3 right-3 p-1 hover:bg-[#111] hover:text-white transition-colors"
          aria-label="Close sign in"
        >
          <X size={16} strokeWidth={2.6} />
        </button>

        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold mb-2">
          {mode === "signin" ? "01 — Sign in" : "01 — Create account"}
        </div>
        <h2 className="font-display text-3xl font-extrabold text-[#111] leading-tight">
          {mode === "signin" ? "Welcome back." : "Join guidedsport."}
        </h2>
        <p className="mt-2 text-sm text-[#555]">
          {mode === "signin"
            ? "Sign in to unlock saved favorites, parlay slips and tier-locked insights."
            : "Create a free account. Upgrade when you're ready."}
        </p>

        <button
          type="button"
          onClick={loginWithGoogle}
          data-testid="signin-google-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-3 border-2 border-[#111] bg-[#111] text-[#f6f6f4] hover:bg-[#222] px-4 py-3 font-display font-extrabold uppercase tracking-[0.12em] text-sm transition-colors focus-ring active:translate-y-px"
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-[#111] opacity-20" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#777] font-bold">OR</span>
          <span className="flex-1 h-px bg-[#111] opacity-20" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">Email</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="signin-email-input"
              className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">Password</label>
            <input
              type="password"
              required
              minLength={8}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signin-password-input"
              className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
            />
          </div>
          {mode === "signup" && (
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">
                Invite code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                data-testid="signin-invite-input"
                className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
                placeholder="GUIDEDSPORT-2026"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="signin-submit-btn"
            className="w-full inline-flex items-center justify-center gap-3 border-2 border-[#111] bg-transparent px-4 py-3 font-display font-extrabold uppercase tracking-[0.12em] text-sm hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="spin-slow" />
                {mode === "signin" ? "Signing in…" : "Creating account…"}
              </>
            ) : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="mt-4 font-mono text-[11px] tracking-[0.15em] uppercase text-[#555] font-bold text-center">
          {mode === "signin" ? "No account?" : "Already a member?"}{" "}
          <button
            type="button"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            data-testid="signin-mode-toggle"
            className="link-u text-[#111]"
          >
            {mode === "signin" ? "Create one" : "Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
